import Image from "next/image";
import Link from "next/link";
import { SiGithub } from "react-icons/si";
import { content } from "../../data/siteData";
import { projects } from "../../data/projects";
import { localePath } from "../../lib/seo";

const ProjectsSection = ({ locale }) => {
  const t = content[locale].projects;

  return (
    <section
      id="projects"
      className="w-full py-16 md:py-24 px-6 md:px-12 bg-white"
    >
      <div className="max-w-7xl mx-auto flex flex-col gap-10 md:gap-16">
        <h2 className="font-poppins font-extralight text-4xl md:text-5xl lg:text-[64px] text-[#333333] text-left leading-none">
          {t.heading}
        </h2>

        <div className="relative">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-[2px] bg-gray-300 -translate-x-1/2"></div>

          <div className="flex flex-col gap-8 md:gap-32">
            {projects.map((project, index) => {
              const item = project[locale];
              const href = project.slug
                ? localePath(locale, `/projects/${project.slug}`)
                : null;

              return (
                <div
                  key={item.title}
                  className={`relative flex flex-col md:flex-row ${index % 2 === 0 ? "md:justify-start" : "md:justify-end"}`}
                >
                  <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-[#333333] rounded-full z-10"></div>

                  <article
                    className={`w-full md:w-[45%] bg-[#f5f5f5] rounded-lg shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group ${index % 2 === 0 ? "md:mr-auto" : "md:ml-auto"}`}
                  >
                    {project.image && (
                      <Image
                        src={project.image}
                        alt={item.imageAlt}
                        sizes="(min-width: 768px) 45vw, 100vw"
                        className="w-full h-48 md:h-56 object-cover object-top bg-white"
                      />
                    )}
                    <div className="p-6 md:p-8">
                      <div className="flex items-start md:items-center justify-between mb-4 gap-4">
                        <h3 className="font-poppins font-light text-xl md:text-2xl text-[#333333]">
                          {href ? (
                            <Link href={href} className="hover:underline underline-offset-4">
                              {item.title}
                            </Link>
                          ) : (
                            item.title
                          )}
                        </h3>
                        {project.repo && (
                          <a
                            href={project.repo}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#333333] hover:scale-110 transition-transform flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#333333] rounded"
                            aria-label={t.githubLabel(item.title)}
                          >
                            <SiGithub
                              className="text-2xl md:text-3xl"
                              aria-hidden="true"
                            />
                          </a>
                        )}
                        {project.inProgress && (
                          <span className="flex-shrink-0 px-3 py-1 text-xs md:text-sm font-poppins font-light bg-white text-[#333333] rounded-full border border-[#333333]">
                            {t.inProgress}
                          </span>
                        )}
                      </div>
                      {item.period && (
                        <p className="font-poppins font-light text-sm md:text-base text-[#666666] -mt-2 mb-4">
                          {item.period}
                        </p>
                      )}
                      <p className="font-poppins font-light text-sm md:text-base text-[#555555] mb-4">
                        {item.summary}
                      </p>

                      <div className="flex flex-wrap gap-2 mt-4">
                        {project.tech.map((techItem) => (
                          <span
                            key={techItem}
                            className="px-2.5 md:px-3 py-1 text-xs md:text-sm font-poppins font-light bg-white text-[#333333] rounded-full"
                          >
                            {techItem}
                          </span>
                        ))}
                      </div>

                      {href && (
                        <Link
                          href={href}
                          aria-label={`${t.caseStudy}: ${item.title}`}
                          className="inline-block mt-6 font-poppins font-normal text-sm md:text-base text-[#333333] underline underline-offset-4 hover:text-[#555555]"
                        >
                          {t.caseStudy} &rarr;
                        </Link>
                      )}
                    </div>
                  </article>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
